import {
  AiSummaryPreferenceDTO,
  FileRequestApproveApi,
  GetModelsForDynamicFiltersRequest,
  ModelApi,
  ModelCloneDTO,
  ModelsApi,
  UpdatableModelDTO,
} from 'hemwb-api';
import {configuration} from '../getBackendConfiguration';
import {clearBlacklistedMetadata, prepareUpdateMetadata} from './utils';
import {ModelMetadata} from '../modelMetadata/types';
import {ModelFilterState} from '../../components/pages/model/filter';

export const prepareMetadataInRequest = <T extends {metadata: ModelMetadata}>(model: T): T => {
  return {...model, metadata: clearBlacklistedMetadata(model.metadata)};
};

/**
 * Transform ModelFilterState to DynamicFiltersDTO format expected by backend
 * Maps UI filter state to API filter parameter structure
 */
const transformFiltersToDynamicFiltersDTO = (
  filterState: ModelFilterState,
): {filters: {filterParameters: Record<string, string[]>}} => {
  const filterParameters: Record<string, string[]> = {};

  // Map filter properties to backend field names
  if (filterState.modelTypes?.length > 0) {
    filterParameters['model_type'] = filterState.modelTypes;
  }
  if (filterState.global?.length > 0) {
    filterParameters['global'] = filterState.global;
  }
  if (filterState.countries?.length > 0) {
    filterParameters['country'] = filterState.countries;
  }
  if (filterState.stewards?.length > 0) {
    // Extract steward emails
    filterParameters['steward'] = filterState.stewards.map((s) => s.email || s.name);
  }
  if (filterState.creators?.length > 0) {
    // Extract creator IDs (c.id is the unique identifier)
    filterParameters['creator'] = filterState.creators.map((c) => c.id || c.email).filter((c): c is string => !!c);
  }
  if (filterState.products?.length > 0) {
    filterParameters['product'] = filterState.products;
  }
  if (filterState.diseases?.length > 0) {
    filterParameters['disease'] = filterState.diseases;
  }
  if (filterState.type?.length > 0) {
    filterParameters['type'] = filterState.type;
  }
  if (filterState.therapeuticAreas?.length > 0) {
    filterParameters['therapeutic_area'] = filterState.therapeuticAreas;
  }
  if (filterState.platforms?.length > 0) {
    filterParameters['platform'] = filterState.platforms;
  }
  if (filterState.status?.length > 0) {
    filterParameters['status'] = filterState.status;
  }

  return {
    filters: {
      filterParameters,
    },
  };
};

const modelsApi = new ModelsApi(configuration);
const modelApi = new ModelApi(configuration);
const fileRequestApproveApi = new FileRequestApproveApi(configuration);

export const requestModelList = () => modelsApi.getModelList();

export const requestEligibleModelList = () => modelsApi.getCreatorStewardOwnerModels();

export const requestModelListForDynamicFilers = (requestParameters: GetModelsForDynamicFiltersRequest) =>
  modelsApi.getModelsForDynamicFilters(requestParameters);

export const requestModelDetail = (modelUuid: string) => modelsApi.getModel({modelUuid});
export const requestModelNameExists = (model: UpdatableModelDTO) =>
  modelsApi
    .modelExistsByModelParameters({updatableModelDTO: prepareMetadataInRequest(model)})
    .then((value) => value === 'true');
export const requestModelCreate = (model: UpdatableModelDTO) =>
  modelsApi.createModel({updatableModelDTO: prepareMetadataInRequest(model)});
export const requestModelUpdate = (modelUuid: string, model: UpdatableModelDTO) =>
  modelsApi.updateModel({modelUuid, updatableModelDTO: {...model, metadata: prepareUpdateMetadata(model.metadata)}});

export const requestModelPublish = (modelUuid: string) => modelsApi.publishModel({modelUuid});

export const requestModelRetire = (modelUuid: string, reason: string) => modelApi.retireModel({modelUuid, reason});

export const requestModelDownload = (modelUuid: string) => modelsApi.downloadModelRaw({modelUuid});

export const requestModelDownloadAll = (history = true) => modelsApi.downloadAllModelListRaw({history});

export const requestModelDownloadAllWithFilters = (filterState: ModelFilterState) => {
  const dynamicFiltersDTO = transformFiltersToDynamicFiltersDTO(filterState);
  return modelsApi.downloadAllModelListWithFiltersRaw({
    dynamicFiltersDTO: dynamicFiltersDTO as GetModelsForDynamicFiltersRequest,
  });
};

export const requestSendModelAccessRequested = (modelUuid: string, reason: string) =>
  modelsApi.sendModelAccessRequested({modelUuid, reason});

export const approveModelAccessRequest = (modelUuid: string, role: string, userId: string) =>
  modelsApi.approveModelAccessRequest({modelUuid, role, userId});

export const approveFileAccessRequest = (modelUuid: string, fileUuid: string, userId: string, fileName: string) =>
  fileRequestApproveApi.approveFileAccessRequest({modelUuid, fileUuid, userId, fileName});

export const requestModelClone = (modelUuid: string, modelClone: ModelCloneDTO) =>
  modelsApi.cloneModel({
    modelUuid,
    modelCloneDTO: prepareMetadataInRequest(modelClone),
  });

export const requestModelCloneAll = (modelUuid: string) => modelsApi.cloneModelAll({modelUuid});

export const requestModelShareEmail = (modelUuid: string, isids: string[], messageText: string) =>
  modelsApi.sendModelShareEmail({modelUuid, shareInstanceDTO: {isids, messageText}});

export const requestModelDeleteDraft = (modelUuid: string) => modelsApi.deleteDraftModel({modelUuid});

/**
 * HEMT-8938: Update the AI summary opt-out preference for a model entry.
 *
 * API Endpoint: PUT /model/{modelUuid}/ai-summary/preference
 *
 * This endpoint allows users to opt out of or opt back into AI-generated summaries
 * during model creation or from the model detail page. The preference is persisted
 * in the backend and associated with the model entry.
 *
 * @param modelUuid  - UUID of the model
 * @param optedOut   - true to opt out (disable AI summary), false to enable AI summary
 * @returns Promise that resolves when the preference has been successfully updated
 * @throws Error if the API call fails or returns an error status
 *
 * @example
 * // Opt out of AI summary
 * await requestUpdateAiSummaryPreference('model-uuid-123', true);
 *
 * // Re-enable AI summary
 * await requestUpdateAiSummaryPreference('model-uuid-123', false);
 */
export const requestUpdateAiSummaryPreference = (modelUuid: string, optedOut: boolean): Promise<void> => {
  const aiSummaryPreferenceDTO: AiSummaryPreferenceDTO = {optedOut};
  return modelsApi.updateAiSummaryPreference({modelUuid, aiSummaryPreferenceDTO});
};
